import { useState, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTeamContext } from '../context/TeamContext'
import UpgradeModal from './UpgradeModal'
import TeamPermissionMessage from './TeamPermissionMessage'

/**
 * Layout-level guard that blocks write actions when the account is read-only
 * (trial_expired, cancelled, past_due).
 *
 * Three layers of protection:
 * 1. Route guard — redirects away from /new and /edit routes
 * 2. Button text matching — blocks buttons whose text starts with write-action words
 *    (Add, Create, Delete, Save, Submit, Invite, Send, Record, Upload, etc.)
 * 3. Form submission blocking — prevents form submits (except search forms)
 *
 * To force-block a button that doesn't match text patterns: data-write-action
 * To exempt a button that does match: data-readonly-safe
 */

const WRITE_ACTION_PATTERN = /^(add|create|new|save|submit|delete|remove|edit|update|invite|send|record|upload|archive|restore|convert|approve|reject|assign|mark as|start|serve|confirm|publish|cancel sub|apply|schedule|generate|connect|disconnect|enable|disable|revoke|pay|subscribe|upgrade|choose a plan|record|log |set up|complete|finish|accept|decline|withdraw|close issue|resolve)/i

export default function WriteActionGuard({ children }: { children: React.ReactNode }) {
  const { isReadOnly } = useAuth()
  const { isTeamMember, teamRole } = useTeamContext()
  const isViewer = teamRole === 'viewer'
  const blocked = isReadOnly || isViewer
  const [showUpgrade, setShowUpgrade] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Layer 1: Block navigation to write routes
  useEffect(() => {
    if (!blocked) return

    const path = location.pathname
    if (path.includes('/new') || path.endsWith('/edit') || path.includes('/edit/')) {
      navigate(-1)
      setShowUpgrade(true)
    }
  }, [blocked, location.pathname, navigate])

  // Layer 2: Intercept clicks on write-action buttons
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!blocked) return

      const target = e.target as HTMLElement

      // Check for links to write routes
      const link = target.closest('a')
      if (link) {
        const href = link.getAttribute('href') || ''
        if (href.includes('/new') || href.endsWith('/edit') || href.includes('/edit/') || href.includes('add=true')) {
          e.preventDefault()
          e.stopPropagation()
          setShowUpgrade(true)
        }
        return
      }

      const button = target.closest('button')
      const submitInput = target.closest('input[type="submit"]')

      // Block form submit inputs
      if (submitInput) {
        e.preventDefault()
        e.stopPropagation()
        setShowUpgrade(true)
        return
      }

      if (!button) return

      // Allow elements explicitly marked safe
      if (button.closest('[data-readonly-safe]') || button.getAttribute('data-readonly-safe') === 'true') return

      // Block elements explicitly marked as write actions
      if (button.closest('[data-write-action]') || button.getAttribute('data-write-action') === 'true') {
        e.preventDefault()
        e.stopPropagation()
        setShowUpgrade(true)
        return
      }

      // Block submit-type buttons inside forms
      if (button.type === 'submit') {
        const form = button.closest('form')
        // Standalone button (tab, filter, toggle) — not a real form submit, don't block
        if (!form) return
        // Allow search forms
        if (form.getAttribute('role') === 'search' || form.querySelector('input[type="search"]')) return

        e.preventDefault()
        e.stopPropagation()
        setShowUpgrade(true)
        return
      }

      // Check button text for write-action patterns
      const buttonText = button.textContent?.trim() || ''
      if (WRITE_ACTION_PATTERN.test(buttonText)) {
        e.preventDefault()
        e.stopPropagation()
        setShowUpgrade(true)
        return
      }

      // Allow everything else — navigation, filters, tabs, toggles, sort, view, etc.
    },
    [blocked]
  )

  // Layer 3: Block form submissions
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      if (!blocked) return

      const form = e.target as HTMLFormElement
      // Allow search forms
      if (form.getAttribute('role') === 'search' || form.closest('[role="search"]')) return
      if (form.querySelector('input[type="search"]')) return

      e.preventDefault()
      e.stopPropagation()
      setShowUpgrade(true)
    },
    [blocked]
  )

  if (!blocked) return <>{children}</>

  return (
    <>
      <div onClickCapture={handleClick} onSubmitCapture={handleSubmit}>
        {children}
      </div>
      {showUpgrade && (isViewer
        ? <TeamPermissionMessage variant="viewer" onClose={() => setShowUpgrade(false)} />
        : isTeamMember
          ? <TeamPermissionMessage onClose={() => setShowUpgrade(false)} />
          : <UpgradeModal onClose={() => setShowUpgrade(false)} />
      )}
    </>
  )
}
